import React, { Fragment, useEffect, useState } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import { DeleteConfirmationModal } from '@techchange/base';
import { useLanguageContext } from '@techchange/components';
import { messages } from '../../i18n';

import { RESOURCE_TYPES } from '../../constants';
import {
	formatIntlResourceType,
	isResourceActive,
	isResourceContent,
	isResourceOpen,
} from '../../utils';
import CompletionRequirements from '../CompletionRequirements';
import ResourceSettingsModal from '../ResourceSettingsModal';
import ResourceToolbar from '../ResourceToolbar';
import RuleEditorModal from '../RuleEditorModal';

const axiosClient = axios.create({
	withCredentials: true,
});

const propTypes = {

	/**
	 * The resource.
	 */
	resource: PropTypes.shape({

		/**
		 * Unique ID for the resource
		 */
		id: PropTypes.string.isRequired,

		/**
		 * Title for the resource
		 */
		title: PropTypes.string.isRequired,

		/**
		 * Slug for the resource
		 */
		slug: PropTypes.string.isRequired,

		/**
		 * Type of the resource
		 */
		type: PropTypes.string.isRequired,

		/**
		 * The resource weight.
		 */
		weight: PropTypes.number.isRequired,

		/**
		 * The resource data.
		 */
		data: PropTypes.shape({

			/**
			 * The associated form ID (if any) for the resource. (ONLY NEEDED for forms, polls, quizzes)
			 */
			form_id: PropTypes.string,

			/**
			 * The associated form (if any) for the resource. (ONLY NEEDED for forms, polls, quizzes)
			 *
			 * NOTE: This will only be provided if editing the resource and the width changed.
			 */
			form: PropTypes.shape({

				/**
				 * The form ID.
				 */
				id: PropTypes.string.isRequired,

			}),

		}),

		/**
		 * The resource URL.
		 */
		url: PropTypes.string.isRequired,

		/**
		 * The resource scope.
		 */
		scope: PropTypes.string.isRequired,

		/**
		 * The resource scope ID.
		 */
		scope_id: PropTypes.string.isRequired,

		/**
		 * The resource organization ID.
		 */
		organization_id: PropTypes.string.isRequired,

		/**
		 * The children resources (if any).
		 */
		children: PropTypes.arrayOf(PropTypes.shape({

			/**
			 * The child resource ID.
			 */
			id: PropTypes.string,

		})),

	}).isRequired,

	/**
	 * The parent resource.
	 */
	parent: PropTypes.shape({

		/**
		 * The parent resource ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The parent resource title.
		 */
		title: PropTypes.string.isRequired,

		/**
		 * The parent resource slug.
		 */
		slug: PropTypes.string.isRequired,

		/**
		 * The parent resource type.
		 */
		type: PropTypes.string.isRequired,

		/**
		 * The parent resource URL.
		 */
		url: PropTypes.string.isRequired,

		/**
		 * The children resources for the parent.
		 */
		children: PropTypes.arrayOf(PropTypes.shape({

			/**
			 * The child resource ID.
			 */
			id: PropTypes.string,

		})),

	}),

	/**
	 * The current user's ID (if logged-in).
	 */
	userId: PropTypes.string,

	/**
	 * True, if the current user is an administrator for the scope (defaults to false).
	 */
	isAdmin: PropTypes.bool.isRequired,

	/**
	 * The current user's timezone.
	 */
	userTimezone: PropTypes.string.isRequired,

	/**
	 * True, if the resource if being edited by the current
	 * user on the initial load (defaults to false).
	 *
	 * NOTE: This prop is used to maintain the editing state of the content resource
	 *       if local changes would cause this component to re-render.
	 */
	isEditingOnInitialLoad: PropTypes.bool.isRequired,

	/**
	 * The location.pathname, provided by react-router-dom.
	 */
	pathname: PropTypes.string.isRequired,

	/**
	 * True, if the resource supports completion tracking (defaults to false).
	 */
	isUsingCompletionTracking: PropTypes.bool.isRequired,

	/**
	 * A map of object ID -> rule/condition/subcondition/trackers for each rule in the scope.
	 */
	rulesByObjectID: PropTypes.objectOf(PropTypes.shape({

		/**
		 * The rule ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The rule condition.
		 */
		condition: PropTypes.shape({

			/**
			 * The condition ID.
			 */
			id: PropTypes.string.isRequired,

			/**
			 * The rule subconditions.
			 */
			subconditions: PropTypes.arrayOf(PropTypes.shape({

				/**
				 * The subcondition ID.
				 */
				id: PropTypes.string.isRequired,

				/**
				 * The subcondition completion tracker (if any) for the current user.
				 */
				tracker: PropTypes.shape({

					/**
					 * The completion tracker status.
					 */
					status: PropTypes.string.isRequired,

				}),

			})).isRequired,

			/**
			 * The condition completion tracker (if any) for the current user.
			 */
			tracker: PropTypes.shape({

				/**
				 * The completion tracker status.
				 */
				status: PropTypes.string.isRequired,

			}),

		}).isRequired,

		/**
		 * The rule completion tracker (if any) for the current user.
		 */
		tracker: PropTypes.shape({

			/**
			 * The completion tracker status.
			 */
			status: PropTypes.string.isRequired,

		}),

	})),

	/**
	 * A map of ID -> progress percentage of the user for each rule in the scope.
	 */
	progressByRuleID: PropTypes.objectOf(PropTypes.number),

	/**
	 * The list of gravity forms (DEPRECATED) for the scope.
	 */
	gravityForms: PropTypes.arrayOf(PropTypes.shape({

		title: PropTypes.string.isRequired,

		tc_uuid: PropTypes.string.isRequired,

	})),

	/**
	 * Callback function (returning a promise) to handle deleting the resource.
	 */
	onDeleteResource: PropTypes.func,

	/**
	 * Callback function (returning a promise) to handle saving the resource.
	 */
	onSaveResource: PropTypes.func,

	/**
	 * Callback function (returning a promise) to handle saving the rule for the resource.
	 */
	onSaveRule: PropTypes.func,

};

const defaultProps = {
	rulesByObjectID: {}, // NOTE: completion tracking is optional
	progressByRuleID: {}, // NOTE: completion tracking is optional
	isAdmin: false,
	isEditingOnInitialLoad: false,
	isUsingCompletionTracking: false,
};

/**
 * withResourceLogic
 *
 * A higher order component (HOC) that adds resource logic to make handling resources easier.
 *
 * @function withResourceLogic
 * @param    {string} WrappedComponent  The component to be wrapped.
 * @returns  {string}                   The resulting HTML for the DOM.
 */
export default function withResourceLogic(WrappedComponent) {

	const ResourceLogic = (props) => {

		const {
			isEditingOnInitialLoad,
			onDeleteResource,
			onSaveResource,
			onSaveRule,
			...restProps
		} = props;

		const {
			resource,
			parent,
			userId,
			userTimezone,
			isAdmin,
			isUsingCompletionTracking,
			rulesByObjectID,
			progressByRuleID,
			gravityForms, // DEPRECATED
			pathname,
		} = restProps;

		const intl = useIntl();

		const { defaultLanguage, preferredLanguage } = useLanguageContext();

		// Get the associated rule (if any) for the resource
		const rule = rulesByObjectID[resource.id];

		// The associated form (if any) for the resource (ONLY NEEDED for forms, polls, quizzes)
		const [associatedForm, setAssociatedForm] = useState(resource.data?.form);

		const [isCreating, setIsCreating] = useState(isAdmin && !resource.created_at);
		const [isDeleting, setIsDeleting] = useState(false);
		const [isEditing, setIsEditing] = useState(isAdmin && (isCreating || isEditingOnInitialLoad));
		const [isEditingRule, setIsEditingRule] = useState(false);
		const [isEditingSettings, setIsEditingSettings] = useState(false);
		const [isSaving, setIsSaving] = useState(false);

		// Error when saving the resource
		const [errorMsg, setErrorMsg] = useState(null);
		// Error when saving the settings
		const [showSettingsError, setShowSettingsError] = useState(false);

		// Error when saving the rule
		const [showRuleError, setShowRuleError] = useState(false);
		// Success when saving the rule
		const [showRuleSuccess, setShowRuleSuccess] = useState(false);

		const [showToolbar, setShowToolbar] = useState(false);
		const [showRequirements, setShowRequirements] = useState(false);

		// Fetch the associated form (if needed) for the resource
		useEffect(() => {

			if (resource.data?.form_id &&
				(resource.type === RESOURCE_TYPES.FORM ||
				resource.type === RESOURCE_TYPES.POLL ||
				resource.type === RESOURCE_TYPES.QUIZ) &&
				(associatedForm?.language !== preferredLanguage)
			) {

				axiosClient.get(`/api/v2/forms/${resource.data.form_id}`, {
					params: {
						language: preferredLanguage,
						scope: resource.scope,
						scope_id: resource.scope_id,
					},
				}).then((response) => {

					if (response?.data) {

						setAssociatedForm({
							...response.data,
							language: preferredLanguage,
						});

					}

				}).catch((error) => {
					console.error(error);
				});

			}

		}, [resource, associatedForm, preferredLanguage]);

		const handleCancel = () => {

			// Check if cancelling the creation of a new resource
			if (isCreating) {
				onDeleteResource(parent, resource);
			}

			setIsCreating(false);
			setIsEditing(false);
			setIsSaving(false);

			setErrorMsg(null);

		};

		const handleEdit = () => {

			setIsEditing(true);
			setIsSaving(false);

			setErrorMsg(null);
			setShowToolbar(false);

		};

		const handleSaveAssociatedForm = (data) => {

			return new Promise((resolve) => {

				// Check if this resource type doesn't need an associated form
				if (!data ||
					(data.type !== RESOURCE_TYPES.FORM &&
					data.type !== RESOURCE_TYPES.POLL &&
					data.type !== RESOURCE_TYPES.QUIZ)) {
					return resolve(null);
				}

				let promise;

				// Check if editing an existing associated form for the resource
				if (data.id) {

					promise = axiosClient.patch(`/api/v2/forms/${data.id}`, data);

				// Otherwise, create a new associated form for the resource
				} else {

					promise = axiosClient.post('/api/v2/forms', data);

				}

				return promise.then((response) => {

					if (response?.data) {

						setAssociatedForm(response.data);

						return resolve(response.data);

					}

					return resolve(null);

				});

			});

		};

		/**
		 * Fetch the translated contents for a resource when the language changes.
		 *
		 * NOTE: Only used by admins when editing non form|poll|quiz resources.
		 *
		 * @param {String} childID  The resource ID.
		 * @param {String} language  The new language.
		 *
		 * @returns {Promise} A promise to resolve with the updated resource.
		 */
		const handleOnLanguageChange = (childID, language) => {

			setErrorMsg(null);

			return axiosClient.get(`/api/v2/resources/${childID}`, {
				params: {
					language,
					scope: parent.scope,
					scope_id: parent.scope_id,
				}
			}).then((updatedResource) => {

				return updatedResource;

			}).catch((error) => {

				console.error(error);

				setErrorMsg(intl.formatMessage(messages.TC_COMPONENTS_WITH_RESOURCE_LOGIC_ERROR_MSG_LOAD_RESOURCE));

			});

		};

		const handleSave = (data, formData) => {

			// Make sure that there is at least one resource content in the default language
			const defaultTranslation =
				data.resource_contents?.find((rContent) => rContent.language === defaultLanguage);

			if (!defaultTranslation &&
				data.type !== RESOURCE_TYPES.FORM &&
				data.type !== RESOURCE_TYPES.POLL &&
				data.type !== RESOURCE_TYPES.QUIZ
			) {
				setErrorMsg(intl.formatMessage(messages.TC_COMPONENTS_WITH_RESOURCE_LOGIC_ERROR_MSG_MISSING_DEFAULT_TRANSLATION));
				return Promise.resolve();
			}

			const resourceData = {
				...resource,
				title: data.title,
				content: data.content,
				data: data.data,
				width: parseInt(data.width, 10) || 12,
				creator_id: isCreating ? userId : resource.creator_id,
				parent_id: isCreating ? parent.id : null,
				language: data.language,
				resource_contents: data.resource_contents,
				deleted_resource_contents: data.deleted_resource_contents,
			};

			setErrorMsg(null);
			setIsSaving(true);

			// Persist the changes to the associated form (if needed)
			return handleSaveAssociatedForm(formData).then((updatedForm) => {

				if (updatedForm) {
					resourceData.data.form_id = updatedForm.id;
				}

				// Persist the changes to the resource
				return onSaveResource(parent, resourceData).then((updatedResource) => {

					setIsCreating(false);
					setIsEditing(false);
					setIsSaving(false);

					return updatedResource;

				});

			}).catch((error) => {

				console.error(error);

				setIsSaving(false);
				setErrorMsg(intl.formatMessage(
					messages.TC_COMPONENTS_RESOURCE_CONTENT_GRID_ERROR_MSG_SAVE_RESOURCE_WITH_TYPE, {
						type: resourceData.type,
					}));

			});

		};

		const handleSaveRule = (data) => {

			setShowRuleError(false);
			setShowRuleSuccess(false);

			// Double check that there is actually a rule to save
			if (!data) {

				setIsEditingRule(false);
				return null;

			}

			return onSaveRule({
				...data,
				scope: resource.scope,
				scope_id: resource.scope_id,
			}).then(() => {

				setShowRuleSuccess(true);

			}).catch((error) => {

				console.error(error);

				setShowRuleError(true);

			});

		};

		const handleSaveSettings = (data) => {

			const openDate = data.open_date === '' ? null : data.open_date;
			const defaultChildID = data.default_child_id === '' ? null : data.default_child_id;

			const updatedData = {
				...resource,
				open_date: openDate,
				default_child_id: defaultChildID,
				show_title: data.show_title || false,
			};

			setShowSettingsError(false);

			// Update the settings for the resource!
			return onSaveResource(parent, updatedData).then(() => {

				// Update the associated form (if needed) for the resource!
				return handleSaveAssociatedForm({
					id: resource.data?.form_id,
					type: resource.type,
					allow_editing_submissions: data.allow_editing_submissions,
					allow_multiple_submissions: data.allow_multiple_submissions,
					quiz_result_pass_percentage: data.quiz_result_pass_percentage,
					quiz_result_show_correct_answers: data.quiz_result_show_correct_answers,
					scope: resource.scope,
					scope_id: resource.scope_id,
				}).then(() => {

					setIsEditingSettings(false);

				});

			}).catch((error) => {

				console.error(error);

				setShowSettingsError(true);

			});

		};

		const handleToggleDelete = () => {

			setShowToolbar(false);

			setIsDeleting((prev) => !prev);
			setIsEditingSettings(false);

			setShowSettingsError(false);

		};

		const handleToggleEditRule = () => {

			setShowToolbar(false);

			setIsEditingRule((prev) => !prev);

			setShowRuleError(false);
			setShowRuleSuccess(false);

		};

		const handleToggleEditSettings = () => {

			setShowToolbar(false);

			setIsEditingSettings((prev) => !prev);

			setShowSettingsError(false);

		};

		// Check if the resource supports editing rules for completion-tracking
		const canEditRule =
			isUsingCompletionTracking &&
			resource.type !== RESOURCE_TYPES.MODULE &&
			resource.type !== RESOURCE_TYPES.SUBMODULE &&
			resource.type !== RESOURCE_TYPES.SUBMODULE_CONTENT;

		// Calculate the current user's progress for the rule (if any) for the resource
		const progress = (rule && progressByRuleID[rule.id]) || 0;

		// Calculate the current user's completion status for the rule (if any) for the resource
		const trackerStatus = rule?.tracker?.status || null;

		// Check if the resource should NOT support edit/deletion via the toolbar
		const isRootResource =
			resource.type === RESOURCE_TYPES.PAGE ||
			resource.type === RESOURCE_TYPES.SESSION;

		// Check if the completion requirements should be shown to the user
		const showCompletionRequirements =
			isUsingCompletionTracking &&
			rule?.active &&
			rule.condition?.active &&
			rule.condition.subconditions;

		// Check if the resource content is completely hidden for the current user
		// (i.e. unable to view because it is closed)
		if (!isResourceOpen(resource) &&
			isResourceContent(resource) &&
			!isAdmin) {

			return null;

		}

		const toolbarPopoverContainerId =
			`${resource.type}-${resource.id}-toolbar-container`;

		const toolbarPopoverTargetId =
			`${resource.type}-${resource.id}-toolbar`;

		const completionRequirementsPopoverTargetId =
			`${resource.type}-tracker-${resource.id}`;

		return (
			<span>
				<WrappedComponent
					active={isResourceActive(pathname, resource)}
					isCreating={isCreating}
					isEditing={isEditing}
					isSaving={isSaving}
					errorMsg={errorMsg}
					progress={progress}
					trackerStatus={trackerStatus}
					rule={rule}
					form={associatedForm}
					toolbarPopoverTargetId={toolbarPopoverTargetId}
					toolbarPopoverContainerId={toolbarPopoverContainerId}
					completionRequirementsPopoverTargetId={
						showCompletionRequirements
							? completionRequirementsPopoverTargetId
							: null
					}
					showRequirementsPopover={showRequirements}
					showToolbarPopover={showToolbar}
					onLanguageChange={handleOnLanguageChange}
					onCancel={handleCancel}
					onSave={handleSave}
					{...restProps} />
				{!isRootResource &&
					<Fragment>
						{isAdmin &&
							<ResourceToolbar
								resource={resource}
								popoverTargetId={toolbarPopoverTargetId}
								popoverContainerId={toolbarPopoverContainerId}
								isAdmin={isAdmin}
								isEditing={isEditing}
								isOpen={showToolbar}
								setShowToolbar={setShowToolbar}
								onEdit={handleEdit}
								onEditRule={canEditRule ? handleToggleEditRule : undefined}
								onEditSettings={handleToggleEditSettings} />
						}
						{showCompletionRequirements &&
							<CompletionRequirements
								resource={resource}
								rule={rule}
								isOpen={showRequirements}
								setShowRequirements={setShowRequirements}
								rulesByObjectID={rulesByObjectID}
								progressByRuleID={progressByRuleID}
								baseURL={'/'} // TODO
								popoverTargetId={completionRequirementsPopoverTargetId}
								onToggle={() => setShowRequirements((prev) => !prev)} />
						}
						{isAdmin && isUsingCompletionTracking &&
							<RuleEditorModal
								resource={resource}
								parent={parent}
								rule={rule}
								userId={userId}
								gravityForms={gravityForms} // DEPRECATED
								submitError={showRuleError}
								submitSuccess={showRuleSuccess}
								isOpen={isEditingRule}
								onSave={handleSaveRule}
								onToggle={handleToggleEditRule} />
						}
						{isAdmin &&
							<ResourceSettingsModal
								resource={resource}
								form={associatedForm}
								userTimezone={userTimezone}
								isOpen={isEditingSettings}
								submitError={showSettingsError}
								onDelete={handleToggleDelete}
								onSave={handleSaveSettings}
								onToggle={handleToggleEditSettings} />
						}
						{isAdmin &&
							<DeleteConfirmationModal
								type={formatIntlResourceType(intl, resource.type)}
								title={resource.title}
								warning={intl.formatMessage(messages.TC_COMPONENTS_WITH_RESOURCE_LOGIC_WARNING_MSG_DELETE_RESOURCE, {
									title: resource.title,
									type: formatIntlResourceType(intl, resource.type),
									bold: (chunk) => <b>{chunk}</b>,
								})}
								size="lg"
								isOpen={isDeleting}
								onCancel={handleToggleDelete}
								onSubmit={() => onDeleteResource(parent, resource)} />
						}
					</Fragment>
				}
			</span>
		);

	};

	ResourceLogic.propTypes = propTypes;
	ResourceLogic.defaultProps = defaultProps;

	return ResourceLogic;

}
